import { createContext, useContext, type ReactNode } from "react";

import {
  DENSE_TABLE_ROW_HEIGHT_CLASS,
  TABLE_ROW_HEIGHT_CLASS,
} from "../constants/layout";

/**
 * How much air every card on the page carries. `comfortable` is what V1 and V2
 * shipped with; `compact` is V3's density pass on rows, charts, and copy —
 * card padding and the gutter between cards stay at 24px so the page doesn't
 * tighten around the figures.
 *
 * A context rather than a prop because half the cards that need it never
 * receive `version`: the store-type chart, the three cumulative tables, and the
 * cohort page are all mounted from tab components that only pass data. Threading
 * a flag through those would touch every call site to say one thing about
 * spacing, and a card that was missed would be invisible until someone noticed
 * it sitting 8px off its neighbors.
 */
type CardDensity = "comfortable" | "compact";

const CardDensityContext = createContext<CardDensity>("comfortable");

export function CardDensityProvider({
  density,
  children,
}: {
  density: CardDensity;
  children: ReactNode;
}) {
  return (
    <CardDensityContext.Provider value={density}>
      {children}
    </CardDensityContext.Provider>
  );
}

export function useIsCompact(): boolean {
  return useContext(CardDensityContext) === "compact";
}

/** Padding inside a card: 24px on every version. */
export function useCardPaddingClass(): string {
  return "p-6";
}

/** Space between stacked cards, and between the metrics inside one. */
export function useStackGapClass(): string {
  return "gap-6";
}

/**
 * Table row height. Acts as a minimum, so the month table's two-line current
 * month row still grows past it rather than clipping.
 */
export function useTableRowHeightClass(): string {
  return useIsCompact() ? DENSE_TABLE_ROW_HEIGHT_CLASS : TABLE_ROW_HEIGHT_CLASS;
}
